import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import Callout from '@/components/General/Callout';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import CutCard from '@/components/General/CutCard';
import FollowUpHeader from '@/components/FollowUp/FollowUpHeader';
import AudienceEditorSheet from '@/components/FollowUp/AudienceEditorSheet';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule } from '@/utils/organizationEntitlements';
import { showAlert } from '@/utils/showAlert';

/**
 * /follow-up/audiences — manager-only (Manage Follow Up) audience
 * configuration surface (spec §6.1). Audiences choose WHO is monitored
 * (optional people filter + attendance-event scope) and carry their own
 * attention thresholds — there is no org-wide policy anymore.
 *
 * With zero enabled audiences monitoring is effectively off, so the empty
 * state says that honestly. Deleting is destructive but honest: FK links
 * SET NULL server-side and people explicitly included stay monitored.
 */

const AudienceRow = ({ audience, onEdit, onDelete }) => {
    const eventCount = (audience?.attendanceEventIDs || []).length;
    const hasCustomFilter = !!audience?.filterJSON
        && (typeof audience.filterJSON !== 'object' || Object.keys(audience.filterJSON).length > 0);

    const menuOptions = [
        { name: 'Edit', icon: dropdownIcons.edit, onSelect: onEdit },
        { name: 'Delete', icon: dropdownIcons.delete, destructive: true, onSelect: onDelete },
    ];

    return (
        <Pressable
            testID={`audience-${audience.id}`}
            accessibilityRole="button"
            onPress={onEdit}
            style={styles.audienceRow}
        >
            <View style={styles.audienceIcon}>
                <Unicon name="people-outline" size={16} colorVariant="paths" />
            </View>
            <View style={styles.audienceBody}>
                <Text style={styles.audienceName} numberOfLines={1}>{audience.name}</Text>
                <View style={styles.audienceChips}>
                    <Chip
                        icon={audience.isActive === false ? 'radio-button-off' : 'radio-button-on'}
                        colorVariant={audience.isActive === false ? 'textSecondary' : 'success'}
                    >
                        {audience.isActive === false ? 'Inactive' : 'Active'}
                    </Chip>
                    {eventCount > 0 && (
                        <Chip icon="calendar-outline" colorVariant="textSecondary">
                            {`${eventCount} qualifying event${eventCount === 1 ? '' : 's'}`}
                        </Chip>
                    )}
                    {hasCustomFilter && (
                        <Chip icon="funnel-outline" colorVariant="textSecondary">{`Filter: ${audience.filterName || 'custom'}`}</Chip>
                    )}
                </View>
            </View>
            <DropdownMenu testID={`audience-menu-${audience.id}`} options={menuOptions} />
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

export default function FollowUpAudiencesScreen() {
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canManage && moduleAllowed;

    const { data: audiences = [], isLoading: audiencesLoading } = Query.followUp.audiences({
        organizationID,
        options: { enabled: gated },
    });

    const [editingAudience, setEditingAudience] = useState(null); // {} = new
    const [audienceSheetOpen, setAudienceSheetOpen] = useState(false);

    const deleteMutation = Mutation.followUp.deleteAudience({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: 'Audience deleted' });
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to delete the audience. Please try again.' });
            }
        },
    });

    const confirmDelete = (audience) => {
        showAlert({
            title: 'Delete audience?',
            subtitle: `Deleting "${audience.name}" removes monitoring for this audience — people explicitly included stay monitored.`,
            isDestructive: true,
            confirmText: 'Delete',
            onConfirm: () => deleteMutation.mutate({ organizationID, audienceID: audience.id }),
        });
    };

    const openNewAudience = () => {
        setEditingAudience(null);
        setAudienceSheetOpen(true);
    };
    const openEditAudience = (audience) => {
        setEditingAudience(audience);
        setAudienceSheetOpen(true);
    };

    const sortedAudiences = useMemo(
        () => [...audiences].sort((a, b) => `${a.name || ''}`.localeCompare(`${b.name || ''}`)),
        [audiences]
    );

    if (loggedInUser && (!canManage || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed
                        ? 'Follow Up is not available on this plan'
                        : "Follow Up audiences require the Manage Follow Up permission. Contact your Reach administrator."}
                />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <ScrollView style={styles.scroll} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
                <FollowUpHeader active="audiences" canManage={canManage} />

                {/* Audiences */}
                <View style={styles.sectionHeader}>
                    <View style={styles.sectionTitleWrap}>
                        <Text style={styles.sectionTitle}>Audiences</Text>
                        <Text style={styles.sectionHint}>
                            Audiences choose who is monitored and each audience&rsquo;s attention thresholds. People can also be included or excluded individually on their profile.
                        </Text>
                    </View>
                    <SolidButton
                        testID="audience-create"
                        size="sm"
                        variant="outlined"
                        type="paths"
                        iconName="add"
                        text="New Audience"
                        onPress={openNewAudience}
                    />
                </View>
                <CutCard accentColor="paths" style={styles.card}>
                    {audiencesLoading ? (
                        <ActivityIndicator />
                    ) : sortedAudiences.length === 0 ? (
                        <View style={styles.emptyWrap}>
                            <Text style={styles.emptyText}>
                                No audiences configured.
                            </Text>
                            <Callout
                                type="info"
                                testID="audiences-monitoring-off"
                                message="Monitoring is off until at least one audience is enabled."
                            />
                        </View>
                    ) : (
                        sortedAudiences.map((audience) => (
                            <AudienceRow
                                key={audience.id}
                                audience={audience}
                                onEdit={() => openEditAudience(audience)}
                                onDelete={() => confirmDelete(audience)}
                            />
                        ))
                    )}
                </CutCard>

                <Text style={styles.sectionHint}>
                    Team and manager access is managed through Roles &amp; Permissions ({'View Follow Up'} / {'Manage Follow Up'}).
                </Text>
            </ScrollView>

            <AudienceEditorSheet
                isOpen={audienceSheetOpen}
                onClose={() => setAudienceSheetOpen(false)}
                organizationID={organizationID}
                audience={editingAudience}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 16, paddingBottom: 60, gap: 12, maxWidth: 1180, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 8,
        paddingHorizontal: 4,
    },
    sectionTitleWrap: { flex: 1, minWidth: 0, gap: 2 },
    sectionTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    sectionHint: {
        fontSize: 12,
        color: colors.textSecondary,
        paddingHorizontal: 4,
        lineHeight: 17,
    },
    card: {
        padding: 14,
        gap: 10,
        boxShadow: shadows.card,
    },
    emptyWrap: { gap: 8 },
    emptyText: { fontSize: 13, color: colors.textSecondary, fontStyle: 'italic' },
    audienceRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 6,
        _web: { cursor: 'pointer' },
    },
    audienceIcon: {
        width: 30,
        height: 30,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    audienceBody: { flex: 1, minWidth: 0, gap: 4 },
    audienceName: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    audienceChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
}));
